import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  ChevronDown,
  User,
  LogOut,
  Settings,
  Search,
  MessageSquare,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const EMBEDDED_SIGNUP_CONFIG_ID = "1404119364652323";
const REDIRECT_URI = "https://90c56c829ca4.ngrok-free.app/whatsapp/callback";

const Header = ({ toggleSidebar }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState("User");
  const [userInitial, setUserInitial] = useState("U");
  const [pageTitle, setPageTitle] = useState("Dashboard");
  const [pageSubtitle, setPageSubtitle] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [wabaAccounts, setWabaAccounts] = useState([]);
  const [selectedWaba, setSelectedWaba] = useState(null);
  const [loadingWabaAccounts, setLoadingWabaAccounts] = useState(false);
  const dropdownRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Update page title
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("dashboard")) {
      setPageTitle("Dashboard");
      setPageSubtitle("Overview of your messaging analytics");
    } else if (path.includes("main") || path.includes("instance")) {
      setPageTitle("Instances");
      setPageSubtitle("Manage your WhatsApp instances");
    } else if (path.includes("templates")) {
      setPageTitle("Templates");
      setPageSubtitle("Manage message templates");
    } else if (path.includes("broadcast")) {
      setPageTitle("Broadcasting");
      setPageSubtitle("Send bulk WhatsApp messages");
    } else if (path.includes("flow-list")) {
      setPageTitle("Flow Lists");
      setPageSubtitle("Manage your automation flows");
    } else if (path.includes("live-chats")) {
      setPageTitle("Live Chats");
      setPageSubtitle("Manage real-time conversations");
    } else if (path.includes("docs")) {
      setPageTitle("API Documentation");
      setPageSubtitle("Integration guides and references");
    } else if (path.includes("profile")) {
      setPageTitle("Your Profile");
      setPageSubtitle("Manage your account settings");
    } else if (path.includes("settings")) {
      setPageTitle("Settings");
      setPageSubtitle("Configure your preferences");
    } else {
      setPageTitle("Dashboard");
      setPageSubtitle("");
    }
  }, [location]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.name) {
          setUserName(parsedUser.name);
          setUserInitial(parsedUser.name.charAt(0).toUpperCase());
        }
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
  }, []);

  const toggleUserMenu = () => {
    setShowUserMenu((v) => !v);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userAccessToken");
    localStorage.removeItem("user_id");
    window.location.href = "/login";
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.location.reload();
    setTimeout(() => setIsRefreshing(false), 2000);
  };

  // Fetch WABA accounts
  const fetchWabaAccounts = async () => {
    try {
      setLoadingWabaAccounts(true);
      const token = localStorage.getItem("userAccessToken");
      if (!token) return;

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/whatsapp-accounts`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();

      if (
        data.success &&
        data.data &&
        Array.isArray(data.data) &&
        data.data.length > 0
      ) {
        setWhatsappConnected(true);
        // Flatten accounts - handle both grouped (with phone_numbers) and flat structures
        const flattened = [];

        data.data.forEach((account) => {
          // Check if account has phone_numbers array (grouped structure)
          if (
            account.phone_numbers &&
            Array.isArray(account.phone_numbers) &&
            account.phone_numbers.length > 0
          ) {
            account.phone_numbers.forEach((phone) => {
              flattened.push({
                wabaId: account.id || account.waba_id,
                wabaName:
                  account.account_name ||
                  account.waba_name ||
                  account.business_name ||
                  "Unknown",
                phoneId: phone.phone_id,
                phoneNumber: phone.phone_number,
                phoneName: phone.phone_name || phone.verified_name || "",
              });
            });
          }
          // Handle flat structure (direct account with phone_id)
          else if (account.phone_id) {
            flattened.push({
              wabaId: account.waba_id || account.id,
              wabaName:
                account.waba_name ||
                account.account_name ||
                account.business_name ||
                "Unknown",
              phoneId: account.phone_id,
              phoneNumber: account.phone_number,
              phoneName: account.verified_name || account.phone_name || "",
            });
          }
        });

        setWabaAccounts(flattened);

        // Load selected WABA from localStorage or auto-select first
        const savedWaba = localStorage.getItem("selectedWabaAccount");
        if (savedWaba) {
          try {
            const parsed = JSON.parse(savedWaba);
            const found = flattened.find(
              (acc) => acc.phoneId === parsed.phoneId
            );
            if (found) {
              setSelectedWaba(found);
              localStorage.setItem(
                "selectedWabaAccount",
                JSON.stringify(found)
              );
            } else if (flattened.length > 0) {
              // If saved account not found, select first
              const first = flattened[0];
              setSelectedWaba(first);
              localStorage.setItem(
                "selectedWabaAccount",
                JSON.stringify(first)
              );
              window.dispatchEvent(
                new CustomEvent("wabaAccountChanged", { detail: first })
              );
            }
          } catch (e) {
            console.error("Error parsing saved WABA:", e);
            if (flattened.length > 0) {
              const first = flattened[0];
              setSelectedWaba(first);
              localStorage.setItem(
                "selectedWabaAccount",
                JSON.stringify(first)
              );
              window.dispatchEvent(
                new CustomEvent("wabaAccountChanged", { detail: first })
              );
            }
          }
        } else if (flattened.length > 0) {
          // Auto-select first account
          const first = flattened[0];
          setSelectedWaba(first);
          localStorage.setItem("selectedWabaAccount", JSON.stringify(first));
          window.dispatchEvent(
            new CustomEvent("wabaAccountChanged", { detail: first })
          );
        }
      } else {
        console.log("⚠️ No WABA accounts found or invalid response:", data);
        // Don't set connected to false if there's a response but no data
        if (
          !data.success ||
          !data.data ||
          (Array.isArray(data.data) && data.data.length === 0)
        ) {
          setWhatsappConnected(false);
        }
        setWabaAccounts([]);
        // Keep selected WABA if it exists, otherwise clear
        if (!localStorage.getItem("selectedWabaAccount")) {
          setSelectedWaba(null);
        }
      }
    } catch (err) {
      console.error("❌ Error fetching WABA accounts:", err);
      setWhatsappConnected(false);
      setWabaAccounts([]);
      // Keep selected WABA if it exists, otherwise clear
      if (!localStorage.getItem("selectedWabaAccount")) {
        setSelectedWaba(null);
      }
    } finally {
      setLoadingWabaAccounts(false);
    }
  };

  // Check WhatsApp connection status (kept for backward compatibility)
  const checkWhatsappConnection = async () => {
    await fetchWabaAccounts();
  };

  // Handle WABA account change
  const handleWabaChange = (phoneId) => {
    const selectedAccount = wabaAccounts.find((acc) => acc.phoneId === phoneId);
    if (selectedAccount) {
      setSelectedWaba(selectedAccount);
      localStorage.setItem(
        "selectedWabaAccount",
        JSON.stringify(selectedAccount)
      );
      // Dispatch custom event for same-tab updates
      window.dispatchEvent(
        new CustomEvent("wabaAccountChanged", { detail: selectedAccount })
      );
    }
  };

  // Send code to backend
  const sendCodeToBackend = async (code) => {
    const token = localStorage.getItem("userAccessToken");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/facebook/embedded-signup/callback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ code }),
          credentials: "include",
        }
      );
      const data = await res.json();
      console.log("Backend response:", data);
      setWhatsappLoading(false);
      // Refresh accounts after successful connection
      await fetchWabaAccounts();
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent("wabaAccountsUpdated"));
    } catch (error) {
      console.error("Failed to send signup code to backend:", error);
      setWhatsappLoading(false);
      alert("Failed to complete onboarding. Please try again.");
    }
  };

  // Handle Embedded Signup
  const handleEmbeddedSignup = () => {
    setWhatsappLoading(true);

    const token = localStorage.getItem("userAccessToken");
    if (!token) {
      alert("⚠️ Please log in to your account first.");
      setWhatsappLoading(false);
      return;
    }

    if (window.FB && typeof window.FB.login === "function") {
      window.FB.login(
        (response) => {
          if (response.authResponse && response.authResponse.code) {
            const code = response.authResponse.code;
            console.log("FB login code received:", code);
            sendCodeToBackend(code);
          } else {
            console.warn(
              "No auth code received in FB login response",
              response
            );
            setWhatsappLoading(false);
            alert("Failed to get authorization code. Please try again.");
          }
        },
        {
          config_id: EMBEDDED_SIGNUP_CONFIG_ID,
          response_type: "code",
          override_default_response_type: true,
          redirect_uri: REDIRECT_URI,
        }
      );
    } else {
      setWhatsappLoading(false);
      alert("Facebook SDK not loaded. Please refresh and try again.");
    }
  };

  // Check connection on mount and listen for account updates
  useEffect(() => {
    checkWhatsappConnection();

    // Listen for account updates (e.g., after connecting a new account)
    const handleAccountUpdate = () => {
      fetchWabaAccounts();
    };

    // Listen for custom events and storage changes
    window.addEventListener("wabaAccountsUpdated", handleAccountUpdate);
    window.addEventListener("storage", handleAccountUpdate);

    // Refresh accounts every 30 seconds to catch updates
    const intervalId = setInterval(() => {
      fetchWabaAccounts();
    }, 30000);

    return () => {
      window.removeEventListener("wabaAccountsUpdated", handleAccountUpdate);
      window.removeEventListener("storage", handleAccountUpdate);
      clearInterval(intervalId);
    };
  }, []);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  return (
    <header className="fixed top-0 right-0 left-0 lg:left-20 z-40 h-14 md:h-16 bg-white border-b border-gray-200 shadow-sm">
      <div className="h-full w-full">
        <div className="max-w-full h-full px-3 md:px-4 lg:px-5 flex items-center justify-between">
          {/* Left: menu + title */}
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors duration-200"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-4 h-4 md:w-5 md:h-5" />
            </button>

            <div className="min-w-0 flex-1">
              <h1 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 truncate">
                {pageTitle}
              </h1>
              {pageSubtitle && (
                <p className="hidden sm:block text-[10px] md:text-xs text-gray-500 truncate">
                  {pageSubtitle}
                </p>
              )}
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1.5 md:gap-2">
            {/* WhatsApp Connect Button */}
            {!whatsappConnected && (
              <button
                onClick={handleEmbeddedSignup}
                disabled={whatsappLoading}
                className="flex items-center justify-center gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                title="Connect WhatsApp Business"
              >
                {whatsappLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                    <span className="hidden sm:inline">Connecting...</span>
                  </>
                ) : (
                  <>
                    <MessageSquare size={14} />
                    <span className="hidden sm:inline">Connect WhatsApp</span>
                  </>
                )}
              </button>
            )}

            {/* WABA Account Dropdown - Always show when accounts exist */}
            {loadingWabaAccounts ? (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-300 border-t-gray-600"></div>
                <span className="hidden sm:inline">Loading accounts...</span>
              </div>
            ) : wabaAccounts.length > 0 ? (
              <div className="relative">
                <div className="flex items-center gap-1.5">
                  <select
                    value={
                      selectedWaba?.phoneId || wabaAccounts[0]?.phoneId || ""
                    }
                    onChange={(e) => {
                      if (e.target.value) {
                        handleWabaChange(e.target.value);
                      }
                    }}
                    className="px-2 sm:px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent transition-all duration-200 min-w-[140px] sm:min-w-[200px] max-w-[250px] cursor-pointer"
                    title={
                      selectedWaba
                        ? `${selectedWaba.phoneName} - ${selectedWaba.phoneNumber} - ${selectedWaba.wabaId}`
                        : "Select WhatsApp Business Account"
                    }
                  >
                    {wabaAccounts.map((account) => (
                      <option key={account.phoneId} value={account.phoneId}>
                        {account.phoneName || "Account"} - {account.phoneNumber} - {account.wabaId}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}

            {/* User menu */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleUserMenu}
                className="flex items-center gap-1.5 md:gap-2 rounded-lg bg-gray-50 hover:bg-gray-100 pl-1 pr-2.5 py-1 md:pl-1.5 md:pr-3 md:py-1.5 border border-gray-200 transition-all duration-200"
                aria-haspopup="menu"
                aria-expanded={showUserMenu}
              >
                <div
                  className="w-7 h-7 md:w-8 md:h-8 rounded-full text-white font-bold flex items-center justify-center shadow-sm text-xs"
                  style={{ backgroundColor: "rgb(4, 138, 173)" }}
                >
                  {userInitial}
                </div>
                <span className="hidden sm:block text-xs font-medium text-gray-700 truncate max-w-20 md:max-w-28">
                  {userName}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
              </button>

              {showUserMenu && (
                <div
                  role="menu"
                  className="absolute right-0 mt-1.5 w-52 md:w-56 rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden"
                >
                  <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-900 truncate">
                      {userName}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      Account User
                    </p>
                  </div>
                  <ul className="py-0.5">
                    <li>
                      <button
                        onClick={() => {
                          navigate("/profile");
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        role="menuitem"
                      >
                        <User className="w-3.5 h-3.5" />
                        Your Profile
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => {
                          navigate("/settings");
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        role="menuitem"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        Settings
                      </button>
                    </li>
                    <li className="border-t border-gray-100 mt-0.5">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors duration-200"
                        role="menuitem"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        Sign Out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
